import React, { useState, useEffect } from "react";
import axios, { CancelToken } from "axios";
import useModal from "hooks/modals/useModal";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input";
import SelectNew from "components/select/SelectNew";
import FormLayout from "components/form/FormLayout";
import Spin from "components/spin/spin";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";

import { StatusInputContext } from "./StatusInputContextProvider";
import useUnsavedChangesContainer from "hooks/useUnsavedChangesContainer/useUnsavedChangesContainer";
import { Dictionary, cloneDeep, groupBy } from "lodash";
import { ISelectedEditInput } from "components/draggableEditInput/DraggableEditInput";
import EditStatusInputLayout from "./dragAndDropElements/EditStatusInputLayout";
import { generateInputRowsWithSequentialPositions } from "hooks/editInputDragAndDrop/useDropEditInputRow/useDropEditInputRow";

import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider } from "react-dnd";

import { showError, showSuccess } from "redux/actions/ui";

import {
  GetFacilityStatusInputs,
  PostFacilityStatusInput,
  TPutStatusInput,
  TPutStatusInputPosition,
  PutStatusInput,
  PutStatusInputPositions,
  DeleteStatusInput,
} from "api/rpc/2024-04/facilityAdmin/facility/status";
import { StatusCode } from "api/protocols";

import { TFacilityStatusInput } from "redux/reducers/models/facility";
import { useTranslation } from "react-i18next";

export type TStatusInputRows = Dictionary<TFacilityStatusInput[]>;

export default function StatusInputs() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [statusInputs, setStatusInputs] = useState<TFacilityStatusInput[]>(undefined);

  const [selectedInput, setSelectedInput] = useState<ISelectedEditInput>({
    id: null,
    deletePopupOpen: false,
    editModalOpen: false,
  });

  const {
    state: newInputModal,
    closeModal: closeNewInputModal,
    updateModal: updateNewInputNote,
  } = useModal({
    label: "",
    type: "",
    help_text: "",
    clearOnClose: true,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadInputs(source.token);
    return () => source.cancel();
  }, []);

  async function loadInputs(token?: CancelToken) {
    const inputRes = await GetFacilityStatusInputs(null, true, token);

    if (token && token.reason) {
      return;
    }
    if (inputRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.settings.facility.status_inputs.status_inputs.001")));
      return;
    }

    const inputRows = groupBy(inputRes.data, input => input.row);

    for (const row in inputRows) {
      inputRows[row].sort((prev, next) => prev.column - next.column);
    }

    statusInputContainerActions.set(inputRows);

    setStatusInputs(inputRes.data);
  }

  async function createStatus() {
    const inputRes = await PostFacilityStatusInput(
      {
        label: newInputModal.label,
        type: newInputModal.type,
        help_text: newInputModal.help_text,
      },
      true,
    );

    if (inputRes.status !== StatusCode.OK) {
      dispatch(
        showError(
          inputRes.status === StatusCode.BAD_REQUEST
            ? String(inputRes.data)
            : t("secure.facility.settings.facility.status_inputs.status_inputs.020"),
        ),
      );
      return;
    }

    setStatusInputs([...statusInputs, inputRes.data]);
    closeNewInputModal();
    dispatch(showSuccess(t("secure.facility.settings.facility.status_inputs.status_inputs.002")));

    const inputRows = cloneDeep(statusInputContainer.updatedState);

    // set updated inputs
    const row = String(inputRes.data.row);
    const updatedInputRowsWithInput: TStatusInputRows = {
      ...inputRows,
      [row]: [inputRes.data],
    };
    statusInputContainerActions.set(updatedInputRowsWithInput);
  }

  async function updateStatusInputRowPositions() {
    const updatedStatusInputRows: TStatusInputRows = generateInputRowsWithSequentialPositions(
      statusInputContainer.updatedState,
    );

    const statusInputPositions: TPutStatusInputPosition[] = [];

    Object.keys(updatedStatusInputRows).forEach(row => {
      updatedStatusInputRows[row].forEach(input => {
        statusInputPositions.push({ id: input.id, row: input.row, column: input.column });
      });
    });

    const putStatusInputPositions = await PutStatusInputPositions({
      inputs: statusInputPositions,
    });

    if (putStatusInputPositions.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.settings.facility.status_inputs.status_inputs.003")));
      return undefined;
    }

    statusInputContainerActions.set(updatedStatusInputRows);
    return updatedStatusInputRows;
  }

  async function handleUpdateInput(id: number, label: string, helpText: string, value: string[], isRequired: boolean) {
    let updatedStatusInputRows: TStatusInputRows = undefined;

    if (statusInputContainer.changesExist) {
      updatedStatusInputRows = await updateStatusInputRowPositions();

      if (!updatedStatusInputRows) {
        return;
      }
    }

    updatedStatusInputRows ??= statusInputContainer.updatedState;

    const params: TPutStatusInput = {
      id: id,
      label: label,
      help_text: helpText,
      values: value,
      required: isRequired,
    };

    const res = await PutStatusInput(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.settings.facility.status_inputs.status_inputs.004")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.settings.facility.status_inputs.status_inputs.005")));

    const updatedStatusInput = cloneDeep(res.data);
    const updatedInputRowsWithChanges: TStatusInputRows = cloneDeep(updatedStatusInputRows);

    for (const row in updatedInputRowsWithChanges) {
      const updatedRow = updatedInputRowsWithChanges[row];

      for (let i = 0; i < updatedRow.length; i++) {
        if (updatedRow[i].id === updatedStatusInput.id) {
          updatedRow[i] = updatedStatusInput;
        }
      }
    }

    statusInputContainerActions.set(updatedInputRowsWithChanges);
    setSelectedInput(prev => ({ ...prev, id: null, editModalOpen: false }));
  }

  async function handleRemoveInput(id: number) {
    let updatedInputRows: TStatusInputRows = undefined;

    if (statusInputContainer.changesExist) {
      updatedInputRows = await updateStatusInputRowPositions();
      if (!updatedInputRows) {
        return;
      }
    }

    // only assign right operand if inputRows = null | undefined
    updatedInputRows ??= cloneDeep(statusInputContainer.updatedState);

    const res = await DeleteStatusInput({ id }, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.settings.facility.status_inputs.status_inputs.006")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.settings.facility.status_inputs.status_inputs.007")));
    setSelectedInput(prev => ({ ...prev, id: null, deletePopupOpen: false }));

    // update container context
    let rowDeletingFrom = "";

    Object.keys(updatedInputRows).forEach(row => {
      if (updatedInputRows[row]?.some(input => input.id === id)) {
        rowDeletingFrom = row;
      }
    });

    if (rowDeletingFrom.length === 0) {
      return;
    }

    const { [rowDeletingFrom]: originalRow, ...rest } = updatedInputRows;

    const updatedRow = originalRow
      .filter(input => input.id !== id)
      .map((input, index) => ({ ...input, column: index + 1 }));

    const updatedRowsWithDelete: TStatusInputRows = {
      ...rest,
      ...(updatedRow.length > 0 ? { [rowDeletingFrom]: updatedRow } : {}),
    };

    if (updatedRow.length > 0) {
      statusInputContainerActions.set(updatedRowsWithDelete);
      return;
    }

    const updatedRowsWithShift: TStatusInputRows = {};
    const numericRowDeletingFrom = Number(rowDeletingFrom);

    Object.keys(updatedRowsWithDelete).forEach(row => {
      const numericRow = Number(row);

      if (numericRow > numericRowDeletingFrom) {
        const shiftedRow = numericRow - 1;

        updatedRowsWithShift[String(shiftedRow)] = updatedRowsWithDelete[row].map(input => {
          return {
            ...input,
            row: shiftedRow,
          };
        });
      } else {
        updatedRowsWithShift[row] = updatedRowsWithDelete[row];
      }
    });

    statusInputContainerActions.set(updatedRowsWithShift);
  }

  const [statusInputContainer, statusInputContainerActions] = useUnsavedChangesContainer<TStatusInputRows>();

  const primaryAction = {
    content: t("secure.facility.settings.facility.status_inputs.status_inputs.008"),
    action: () => updateNewInputNote({ isOpen: true }),
  };

  return (
    <Page
      title={t("secure.facility.settings.facility.status_inputs.status_inputs.009")}
      primaryAction={primaryAction}
      notificationBarProps={{
        isVisible: statusInputContainer.changesExist,
        onAction: updateStatusInputRowPositions,
        onCancel: statusInputContainerActions.rollback,
      }}
    >
      <Card>
        <DndProvider backend={HTML5Backend}>
          <StatusInputContext.Provider
            value={{
              selectedInput: selectedInput,
              setSelectedInput: setSelectedInput,
              inputRowsContainer: statusInputContainer,
              inputRowsContainerActions: statusInputContainerActions,
              handleUpdateInput: handleUpdateInput,
              closeInputEditModal: () => setSelectedInput(prev => ({ ...prev, id: null, editModalOpen: false })),
            }}
          >
            {!statusInputContainer.updatedState ? (
              <div style={{ height: "106px", overflow: "hidden", transform: "scale(0.5)" }}>
                <Spin />
              </div>
            ) : (
              <Card>
                <Card.Section>
                  <EditStatusInputLayout />
                </Card.Section>
              </Card>
            )}
          </StatusInputContext.Provider>
        </DndProvider>
      </Card>

      <Sheet
        open={newInputModal.isOpen}
        title={t("secure.facility.settings.facility.status_inputs.status_inputs.010")}
        onCancel={closeNewInputModal}
        okText={t("secure.facility.settings.facility.status_inputs.status_inputs.011")}
        onOk={createStatus}
        size="small"
        overflow
      >
        <FormLayout>
          <FormLayout.Group>
            <SelectNew
              id="new_status_input_type_select"
              label={t("secure.facility.settings.facility.status_inputs.status_inputs.012")}
              options={[
                { label: "Input", id: "input" },
                { label: "Select", id: "select" },
                { label: "Checkbox", id: "checkbox" },
              ]}
              onChange={value => updateNewInputNote({ type: String(value) })}
              value={newInputModal.type}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <Input
              label={t("secure.facility.settings.facility.status_inputs.status_inputs.016")}
              onChange={e => updateNewInputNote({ label: e.target.value })}
              value={newInputModal.label}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <Input
              label={t("secure.facility.settings.facility.status_inputs.status_inputs.017")}
              onChange={e => updateNewInputNote({ help_text: e.target.value })}
              value={newInputModal.help_text}
            />
          </FormLayout.Group>
        </FormLayout>
      </Sheet>

      {/* Remove Input Popup */}
      <Portal isMounted={selectedInput.deletePopupOpen}>
        <Popup
          open={selectedInput.deletePopupOpen}
          type="warning"
          title={t("secure.facility.settings.facility.status_inputs.status_inputs.018")}
          description={t("secure.facility.settings.facility.status_inputs.status_inputs.019")}
          onCancel={() => setSelectedInput(prev => ({ ...prev, id: null, deletePopupOpen: false }))}
          onOk={() => handleRemoveInput(selectedInput.id)}
        />
      </Portal>
    </Page>
  );
}
